"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useSite } from "@/components/ecoBuilder/store/SiteContext";
import { usePageContext } from "@/components/ecoBuilder/renderer/PageContext";
import { EditorField } from "@/config/ecoBuilder/types";
import { ArrowUpRight, ShoppingBag, X, Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { createClient } from "@/utils/supabase/client";
import { createOrder } from '@/actions/order-actions';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useSiteContext } from '@/app/tenant/TenantLayoutWrapper';

// Components
import { Button } from "@/components/ecoBuilder/ui/button";
import { Input } from "@/components/ecoBuilder/ui/input";
import { Label } from "@/components/ecoBuilder/ui/label";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerClose
} from "../../ui/drawer";
import { RichTextRenderer } from "@/components/ecoBuilder/atoms/RichTextRenderer";
import { ALGERIAN_WILAYAS } from '@/lib/data/wilayas';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ecoBuilder/ui/select';

// ------------------------------------------------------------------
// Types & Schema
// ------------------------------------------------------------------

const phoneRegex = /^(05|06|07)[0-9]{8}$/;

interface StickyBottomBarProps {
    id: string;
    buttonText?: string;
    showPrice?: boolean;
    products?: any[]; // Injected
    actionType?: 'scroll' | 'drawer' | 'link';
    targetProductId?: string;
    externalLink?: string;

    // Field Visibility Options
    showName?: boolean;
    showPhone?: boolean;
    showWilaya?: boolean;
    showCommune?: boolean;

    // Colors
    backgroundColor?: string;
    buttonColor?: string;
    textColor?: string;
}

export function StickyBottomBar({
    id,
    buttonText = "أطلب الآن",
    showPrice = true,
    products = [],
    actionType = 'drawer',
    targetProductId,
    externalLink,
    showName = true,
    showPhone = true,
    showWilaya = true,
    showCommune = true,
    backgroundColor,
    buttonColor,
    textColor
}: StickyBottomBarProps) {
    const { isEditor, siteId } = useSite();
    const { themeSettings, ...pageContext } = usePageContext();
    const { siteSlug } = useSiteContext();
    const router = useRouter();

    // Theme Defaults
    const primaryColor = themeSettings?.primaryColor || '#7c3aed';
    const finalButtonColor = buttonColor || primaryColor;
    const finalTextColor = textColor || themeSettings?.textColor || '#09090b';

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [product, setProduct] = useState<any>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dynamicShipping, setDynamicShipping] = useState(0);

    // 1. Resolve Product Logic
    const activeProductId = targetProductId || pageContext?.targetProductId;

    // Effect: Fetch Product Data if ID exists
    useEffect(() => {
        let isMounted = true;
        const fetchProductData = async () => {
            // 1. Check if passed in props (SSR/Editor injection)
            if (products && products.length > 0) {
                const found = activeProductId ? products.find(p => p.id === activeProductId) : products[0];
                if (found) {
                    if (isMounted) setProduct(found);
                    return;
                }
            }

            // 2. Fallback: Fetch from Supabase
            if (!activeProductId && !siteId) {
                if (isMounted) setProduct(null); // No product to fetch
                return;
            }

            try {
                const supabase = createClient();
                let query = supabase.from('products').select('*');

                if (activeProductId) {
                    query = query.eq('id', activeProductId);
                } else {
                    query = query.limit(1); // Default to first product
                }

                const { data } = await query.maybeSingle();
                if (isMounted && data) {
                    setProduct(data);
                } else if (isMounted) {
                    setProduct(null);
                }
            } catch (err) {
                console.error("Error loading product:", err);
                if (isMounted) setFetchError("فشل تحميل بيانات المنتج");
            }
        };

        fetchProductData();
        return () => { isMounted = false; };
    }, [activeProductId, siteId, products]);


    // 2. Resolved Data
    const displayProduct = product || (products && products[0]);
    const finalPrice = displayProduct?.price || 0;
    const finalImage = displayProduct?.images?.[0] || displayProduct?.image_url || "/placeholder-product.jpg";
    const finalName = displayProduct?.name || "اسم المنتج";
    const finalCurrency = displayProduct?.currency || 'DZD';


    // 3. Form Setup (Dynamic Schema based on toggles)
    const checkoutSchema = useMemo(() => {
        return z.object({
            fullName: showName ? z.string().min(2, 'الاسم مطلوب') : z.string().optional(),
            phone: showPhone ? z.string().regex(phoneRegex, 'رقم الهاتف غير صحيح') : z.string().optional(),
            wilaya: showWilaya ? z.string().min(1, 'يرجى اختيار الولاية') : z.string().optional(),
            commune: showCommune ? z.string().optional() : z.string().optional(),
            quantity: z.number().min(1),
        });
    }, [showName, showPhone, showWilaya, showCommune]);

    type CheckoutFormValues = z.infer<typeof checkoutSchema>;

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors }
    } = useForm<CheckoutFormValues>({
        resolver: zodResolver(checkoutSchema),
        defaultValues: {
            quantity: 1,
            fullName: "",
            phone: "",
            wilaya: "",
            commune: ""
        }
    });

    const handleWilayaChange = (code: string) => {
        setValue('wilaya', code);
        const w = ALGERIAN_WILAYAS.find(wil => wil.code === code);
        if (w && w.shippingCost) setDynamicShipping(w.shippingCost);
    };

    // 4. Handle Actions
    const handleAction = () => {
        if (actionType === 'scroll') {
            const formEl = document.querySelector('input[name="customerName"]') || document.querySelector('form');
            if (formEl) {
                (formEl as HTMLElement).focus();
                formEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else if (actionType === 'link' && externalLink) {
            window.location.href = externalLink;
        } else {
            // Drawer
            setIsDrawerOpen(true);
        }
    };

    const onSubmit: SubmitHandler<CheckoutFormValues> = async (data) => {
        if (!siteId) {
            toast.error("خطأ: معرف الموقع مفقود");
            return;
        }
        setIsSubmitting(true);
        try {
            const orderPayload = {
                customerName: data.fullName || 'Guest',
                customerPhone: data.phone || '0000000000',
                customerWilaya: data.wilaya || '',
                customerCommune: data.commune || '',
                customerAddress: "استمارة سريعة",
                items: [{
                    id: displayProduct?.id || 'manual-sticky-item',
                    name: finalName,
                    price: finalPrice,
                    quantity: data.quantity,
                    image: finalImage
                }],
                subtotal: finalPrice * data.quantity,
                shippingCost: dynamicShipping,
                total: (finalPrice * data.quantity) + dynamicShipping,
                paymentMethod: 'cod' as const,
                siteId: siteId
            };

            const result = await createOrder(orderPayload);
            if (result.success) {
                toast.success("تم استلام طلبك بنجاح!");
                setValue('fullName', '');
                setValue('phone', '');
                setValue('wilaya', '');
                setValue('commune', '');
                setValue('quantity', 1);
                setIsDrawerOpen(false);
                const redirectPath = siteSlug
                    ? `/store/${siteSlug}/thank-you?order=${result.orderNumber}`
                    : `/thank-you?order=${result.orderNumber}`;
                router.push(redirectPath);
            } else {
                toast.error(result.error || "فشل في إرسال الطلب");
            }
        } catch (e) {
            toast.error("حدث خطأ غير متوقع");
        } finally {
            setIsSubmitting(false);
        }
    };

    // 5. Render
    if (isEditor) {
        return (
            <div className="p-4 bg-muted/20 border-2 border-dashed border-primary/30 rounded-lg text-center opacity-90 transition-all hover:border-primary/60">
                <div className="flex items-center justify-center gap-2 text-primary font-bold">
                    <ShoppingBag className="w-5 h-5" />
                    <span>شريط الشراء السريع (Sticky Bottom)</span>
                </div>
                <div className="mt-2 flex items-center justify-center gap-2">
                    <Button size="sm" className="pointer-events-none" style={{ backgroundColor: finalButtonColor }}>
                        <RichTextRenderer html={buttonText} inline />
                    </Button>
                </div>
            </div>
        );
    }

    if (!displayProduct && !isEditor) return null; // Don't show if no product found (unless editor)

    return (
        <>
            <div
                id={id}
                className={cn(
                    "fixed bottom-0 left-0 w-full z-[60] md:hidden p-3 safe-area-bottom transition-all duration-300",
                    "bg-background/95 backdrop-blur-xl border-t border-border/10 shadow-[0_-5px_20px_rgba(0,0,0,0.1)]"
                )}
                style={{
                    backgroundColor: backgroundColor || undefined,
                    color: finalTextColor
                }}
                dir="rtl"
            >
                <div className="flex items-center justify-between gap-3 max-w-md mx-auto">
                    {/* Product Info */}
                    <div className="flex flex-col flex-1 min-w-0 pr-1">
                        {showPrice && (
                            <span className="text-sm font-bold leading-tight" style={{ color: finalButtonColor }}>
                                {finalPrice > 0 ? `${finalPrice.toLocaleString()} ${finalCurrency}` : 'السعر عند الطلب'}
                            </span>
                        )}
                        <span className="text-xs opacity-70 line-clamp-1 font-medium">
                            {finalName}
                        </span>
                    </div>

                    {/* Action Button */}
                    <button
                        onClick={handleAction}
                        className="flex-none bg-primary text-primary-foreground font-bold py-2.5 px-6 rounded-xl active:scale-95 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 min-w-[140px]"
                        style={{ backgroundColor: finalButtonColor }}
                    >
                        <RichTextRenderer html={buttonText} inline className="pointer-events-none" />
                        {actionType === 'drawer' ? <ShoppingBag className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* QUICK BUY DRAWER */}
            {actionType === 'drawer' && displayProduct && (
                <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
                    <DrawerContent className="rtl max-h-[90vh]">
                        <div className="mx-auto w-full max-w-md overflow-y-auto">
                            <DrawerHeader className="text-right border-b border-border/10 pb-4">
                                <DrawerTitle className="text-lg font-bold flex items-center justify-between">
                                    <span>إتمام الطلب</span>
                                    <DrawerClose className="p-2 bg-muted/50 rounded-full hover:bg-muted transition-colors">
                                        <X className="w-4 h-4" />
                                    </DrawerClose>
                                </DrawerTitle>
                            </DrawerHeader>

                            <div className="p-4 space-y-6">
                                {/* Product Summary */}
                                <div className="flex gap-4 items-start p-3 bg-muted/20 rounded-xl border border-border/40">
                                    <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-white shrink-0 border border-border/10">
                                        <img src={finalImage} alt={finalName} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0 text-right">
                                        <h4 className="font-bold text-sm line-clamp-2 leading-snug">{finalName}</h4>
                                        <div className="text-primary font-bold mt-1 text-sm" style={{ color: finalButtonColor }}>
                                            {finalPrice > 0 ? `${finalPrice.toLocaleString()} ${finalCurrency}` : 'مجاناً'}
                                        </div>
                                    </div>
                                </div>

                                {/* Order Form */}
                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                    {fetchError && <p className="text-sm text-red-500 text-center mb-4 font-medium">{fetchError}</p>}
                                    {showName && (
                                        <div className="space-y-1.5 text-right">
                                            <Label className="text-xs font-semibold">الاسم الكامل</Label>
                                            <Input {...register('fullName')} placeholder="أدخل اسمك" className={cn("text-right h-11", errors.fullName && "border-red-500")} />
                                            {errors.fullName && <span className="text-[10px] text-red-500">{errors.fullName.message}</span>}
                                        </div>
                                    )}

                                    {showPhone && (
                                        <div className="space-y-1.5 text-right text-right">
                                            <Label className="text-xs font-semibold">رقم الهاتف</Label>
                                            <div className="relative" dir="ltr">
                                                <Input {...register('phone')} placeholder="05 XX XX XX XX" type="tel" className={cn("text-left h-11 pl-4", errors.phone && "border-red-500")} />
                                            </div>
                                            {errors.phone && <span className="text-[10px] text-red-500 block text-right">{errors.phone.message}</span>}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-3">
                                        {showWilaya && (
                                            <div className="space-y-1.5 text-right">
                                                <Label className="text-xs font-semibold">الولاية</Label>
                                                <Select onValueChange={handleWilayaChange}>
                                                    <SelectTrigger className={cn("h-11", errors.wilaya && "border-red-500")}>
                                                        <SelectValue placeholder="اختر ولاية" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {ALGERIAN_WILAYAS.map(w => (
                                                            <SelectItem key={w.code} value={w.code}>{w.code} - {w.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        {showCommune && (
                                            <div className="space-y-1.5 text-right">
                                                <Label className="text-xs font-semibold">البلدية</Label>
                                                <Input {...register('commune')} placeholder="اسم البلدية" className="h-11 text-right" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Quantity */}
                                    <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg border border-border/10 mt-2">
                                        <span className="text-sm font-medium">الكمية:</span>
                                        <div className="flex items-center gap-3 bg-background rounded-md border border-border/20 px-2 py-1">
                                            <button type="button" onClick={() => {
                                                const c = watch('quantity');
                                                if (c > 1) setValue('quantity', c - 1);
                                            }} className="w-6 h-6 flex items-center justify-center font-bold hover:bg-muted rounded text-lg">-</button>

                                            <span className="w-4 text-center font-bold text-sm">{watch('quantity')}</span>

                                            <button type="button" onClick={() => {
                                                const c = watch('quantity');
                                                setValue('quantity', c + 1);
                                            }} className="w-6 h-6 flex items-center justify-center font-bold hover:bg-muted rounded text-lg">+</button>
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full h-12 text-base font-bold shadow-lg mt-4 gap-2 rounded-xl"
                                        style={{ backgroundColor: finalButtonColor }}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
                                            <Check className="w-5 h-5" />
                                            <span>تأكيد الطلب الآن</span>
                                        </>}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </DrawerContent>
                </Drawer>
            )}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════
// REGISTRY EXPORTS
// ═══════════════════════════════════════════════════════════════

export const StickyBottomBarDefaultProps = {
    buttonText: "أطلب الآن",
    showPrice: true,
    actionType: 'drawer',
    showName: true,
    showPhone: true,
    showWilaya: true,
    showCommune: true,
    backgroundColor: "#ffffff",
};

export const StickyBottomBarSchema: EditorField[] = [
    {
        key: "targetProductId",
        label: "المنتج المستهدف",
        type: "product", // Use Smart Product Picker
        description: "اختر المنتج الذي سيتم شراؤه عند الضغط على الزر.",
    },
    {
        key: "actionType",
        label: "نوع الإجراء عند الضغط",
        type: "select",
        options: [
            { value: "drawer", label: "🎁 فتح نافذة شراء سريع (Drawer)" },
            { value: "scroll", label: "⬇️ تمرير لنموذج الطلب (Scroll)" },
            { value: "link", label: "🔗 رابط خارجي (Link)" }
        ],
        defaultValue: "drawer"
    },
    {
        key: "buttonText",
        label: "نص الزر",
        type: "richtext", // Tiptap Enabled
        defaultValue: "أطلب الآن"
    },
    // Form Visibility Toggles
    {
        key: "showName",
        label: "إظهار حقل الاسم",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showPhone",
        label: "إظهار حقل الهاتف",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showWilaya",
        label: "إظهار حقل الولاية",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showCommune",
        label: "إظهار حقل البلدية",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showPrice",
        label: "عرض السعر في الشريط",
        type: "boolean",
        defaultValue: true
    },
    // Styles
    {
        key: 'buttonColor',
        label: 'لون الزر',
        type: 'color',
    },
    {
        key: 'backgroundColor',
        label: 'لون الخلفية',
        type: 'color',
    },
    {
        key: 'textColor',
        label: 'لون النص',
        type: 'color',
    }
];
